import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import clsx from 'classnames';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import { Grid } from '@material-ui/core';
import { vehicleDetails, subscribe, snackShow, unsubscribe } from '../../../actions';
import { PageWrapper } from '../../../components';
import {
  Header,
  AuctionEndTimeSection,
  KeyFeaturesMobile,
  QuestionSection,
  SellerInformationSection,
  PaymentSection,
  RunBuggySection,
  BidHistorySection,
  AuctionSummarySection,
  Equipment,
  VehicleCondition,
  VehicleDetailInfo,
  VehicleInformation,
  ImageSlider,
  VehicleSaleTermsSection,
  DealerCommentsSection,
  ListerAvatarSection
} from './components';
import {
  AUCTION_STATUSES,
  BUYER_VEHICLE_STATUS,
  roles,
  SELLER_VEHICLE_STATUS,
  VEHICLE_STATUSES
} from '../../../constants';
import { WS_VEHICLE_AUCTION_UPDATE } from '../../../constants/actionTypes';
import { directionLink, convertToLocalTime } from '../../../utils';
import { TimeSuggestions, PriceSuggestions } from './SuggestionsBlock';
import { VehicleSidePanel, VehicleSidePanelMobile } from '../../../components/VehicleSidePanel';
import apiSlice from '../../../services/api';
import { legacyOfferAuctionSelector } from 'services/api/offer-auction';
import { useState, useEffect } from 'react';

import './index.scss';
import { setActiveVehicleId } from '../../../services/sidePanel/sidePanel';
import { auctionOfferStatuses } from '../../../utils/statusCalculator';
import { OfferAuctionStatus } from '../../../types/vehicle';
import OfferSummarySection from './components/OfferSummarySection';
import { NegotiationLabel } from 'components/NegotiationLabel';

const styles = {
  marginBottom: {
    marginBottom: '32px'
  },
  dividerMargin: {
    margin: '24px 0'
  }
};

const ContentWrapper = styled.div`
  position: relative;
`;

const ImageSliderWrapper = styled.div`
  position: relative;
`;

const SidePanelWrapper = styled.div`
  position: absolute;
  width: 246px;
  right: 0;
  top: 8px;
  bottom: 0;
  max-height: calc(100% - 20px);
  overflow-y: scroll;

  @media (max-width: 1280px) {
    display: none;
  }
`;

const StyledGalleryHolder = styled(Grid)`
  @media (max-width: 960px) {
    width: 100%;
  }
`;
const StyledMainGrid = styled(Grid)`
  width: 100%;
`;

class VehicleDetails extends Component {
  state = {
    initialLoading: true
  };

  async componentWillMount() {
    const { vehicleNotFound, history, match, fetchDetails, fetchOfferAuction, currentUser } = this.props;
    const { params } = match;

    try {
      await fetchDetails(params.vehicleId, { notDraft: true });
      if (params.vehicleId) {
        fetchOfferAuction(params.vehicleId);
      }
      this.setState({ initialLoading: false });
    } catch (e) {
      console.error(e);
      if (e.response?.status === 404) {
        vehicleNotFound();
        history.push('/');
      }
    }

    const { subscribeToSocket } = this.props;
    subscribeToSocket({
      subscribe: [
        {
          to: `vehicles:${params.vehicleId}`
        },
        {
          to: `user:${currentUser.id}`
        }
      ]
    });
  }

  componentDidMount() {
    const { setActiveVehicle, match } = this.props;
    const { params } = match;
    setActiveVehicle(Number(params.vehicleId));
  }

  componentDidUpdate(prevProps) {
    const { match, fetchDetails, fetchOfferAuction } = this.props;
    const { match: prevMatch } = prevProps;

    if (match.params.vehicleId !== prevMatch.params.vehicleId) {
      const { subscribeToSocket, unsubscribeFromSocket } = this.props;
      unsubscribeFromSocket();
      fetchDetails(match.params.vehicleId, { notDraft: true });
      fetchOfferAuction(match.params.vehicleId);
      window.scrollTo(0, 0);
      subscribeToSocket({
        subscribe: [
          {
            to: `vehicles:${match.params.vehicleId}`
          }
        ]
      });
    }
  }

  componentWillUnmount() {
    const { unsubscribeFromSocket } = this.props;
    unsubscribeFromSocket();
  }

  formatTimeRemaining = date => {
    if (moment().isAfter(moment(date))) {
      return '0h 0m 0s';
    }

    const duration = moment.duration(moment(date).diff(moment()));
    const days = duration.days() ? `${duration.days()}d ` : '0d ';
    const hours = duration.hours() ? `${duration.hours()}h ` : '0h ';
    const minutes = `${duration.minutes()}m`;
    const remaining = `${days}${hours}${minutes}`;

    return remaining;
  };

  render() {
    const { currentUser, updateStatus, vehicle, loading, myBids, offerAuction } = this.props;
    const hasBids = myBids && myBids.ids && myBids.ids.length > 0;
    const { initialLoading } = this.state;
    const isLoading = loading || initialLoading;

    const { user } = vehicle;

    const status = auctionOfferStatuses(vehicle, offerAuction, currentUser);

    const vehicleBids = (vehicle.bids || []).map(bid => ({
      id: bid.id,
      bid: bid.amount,
      bidder: bid.bidder?.dealership_name,
      date: convertToLocalTime(bid.created_at, true),
      created_at: bid.created_at
    }));

    const vehicleOfferBids = (offerAuction?.offerBids || []).map(bid => ({
      id: bid.id,
      bid: bid.amount,
      bidder: bid.bidder?.dealership_name,
      date: convertToLocalTime(bid.created_at, true),
      created_at: bid.created_at
    }));

    const showOfferBidsSection = (() => {
      if (!vehicleOfferBids || vehicleOfferBids.length === 0) return false;
      if (currentUser.role === 'admin') return true;
      if (vehicle.auction?.status === AUCTION_STATUSES.ENDED) return false;
      if (currentUser.role === 'buyer')
        return [
          BUYER_VEHICLE_STATUS.AWARDED,
          BUYER_VEHICLE_STATUS.DECLINED,
          BUYER_VEHICLE_STATUS.AWARDED_NOT_CHARGED,
          BUYER_VEHICLE_STATUS.AWARDED_PENDING_CHARGE,
          BUYER_VEHICLE_STATUS.SELLER_ACCEPTING_OFFERS,
          BUYER_VEHICLE_STATUS.TOP_BUYER_SELLER_ACCEPTING_OFFER
        ].includes(status);
      if (currentUser.role === 'seller') {
        return (
          status === SELLER_VEHICLE_STATUS.AWARDED ||
          (offerAuction?.status === OfferAuctionStatus.PUBLIC_OFFERS &&
            [
              SELLER_VEHICLE_STATUS.NEED_SELLER_REVIEW,
              SELLER_VEHICLE_STATUS.PUBLIC_COUNTER_SENT_TO_BUYERS,
              SELLER_VEHICLE_STATUS.PENDING_ADMIN_APPROVAL,
              SELLER_VEHICLE_STATUS.ACCEPTED_OFFER_PENDING_ADMIN_APPROVAL
            ].includes(status))
        );
      }
    })();

    const shouldRenderOfferSummary = offerAuction?.status === OfferAuctionStatus.PUBLIC_OFFERS;

    const highiestBid = vehicleBids.sort((a, b) => (a.bid <= b.bid && a.id < b.id ? 1 : -1))[0] || {};

    const awardedBid =
      vehicle.status === VEHICLE_STATUSES.AWARDED
        ? vehicleBids.sort((a, b) => (a.id < b.id ? 1 : -1))[0] || null
        : null;

    const shuffleStarted = vehicle.auction && moment.utc().isAfter(vehicle.auction.date_shuffle);
    const SuggestionsBlock = shuffleStarted ? TimeSuggestions : PriceSuggestions;
    const auctionActive = vehicle.auction && vehicle.auction.status === 'active';

    return vehicle && vehicle.id ? (
      <ContentWrapper>
        <div className={clsx('vehicle-details', !hasBids ? 'no-bids' : '')}>
          <Header />
          {!isLoading && (
            <PageWrapper>
              <Grid container spacing={16}>
                <StyledGalleryHolder item lg={7} md={12}>
                  <ImageSliderWrapper>
                    <ImageSlider images={vehicle.images} />
                    <NegotiationLabel offerAuction={offerAuction} vehicle={vehicle} position="top" />
                  </ImageSliderWrapper>
                  <VehicleInformation currentVehicle={vehicle} />
                  <VehicleCondition currentVehicle={vehicle} />
                  <Equipment kbbReportUrl={vehicle.kbb_report_url} options={vehicle.options} />
                </StyledGalleryHolder>
                <StyledMainGrid item lg={5} md={12}>
                  <VehicleDetailInfo vehicle={vehicle} currentUser={currentUser} />
                  {shouldRenderOfferSummary && <OfferSummarySection vehicle={vehicle} currentUser={currentUser} />}
                  {showOfferBidsSection ? (
                    <BidHistorySection bids={vehicleOfferBids} proxyBid={offerAuction?.offerProxyBid} type="offers" />
                  ) : null}
                  <AuctionSummarySection
                    bid={awardedBid ? awardedBid.bid : highiestBid.bid}
                    bidder={awardedBid ? awardedBid.bidder : highiestBid.bidder}
                    remaining={this.formatTimeRemaining(vehicle.auction.date_end)}
                    vehicle={vehicle}
                    currentUser={currentUser}
                    onTimerEnded={updateStatus}
                    initiallyCollapsed={shouldRenderOfferSummary || showOfferBidsSection}
                  />
                  {vehicle.auction.status !== AUCTION_STATUSES.ENDED || currentUser.role === roles.ADMIN ? (
                    <BidHistorySection
                      bids={vehicleBids}
                      proxyBid={vehicle.proxy_bid}
                      initiallyCollapsed={shouldRenderOfferSummary || showOfferBidsSection}
                    />
                  ) : null}
                  <VehicleInformation mobileOnly currentVehicle={vehicle} />
                  <VehicleCondition mobileOnly currentVehicle={vehicle} />
                  <KeyFeaturesMobile options={vehicle.options} currentVehicle={vehicle} />
                  <AuctionEndTimeSection dateEnd={vehicle.date_end || vehicle.auction.date_end} />
                  <SellerInformationSection
                    user={vehicle.user}
                    currentUser={currentUser}
                    directionLink={directionLink}
                  />
                  {currentUser.buyer ? <RunBuggySection vehicleId={vehicle.id} /> : null}
                  <PaymentSection seller={user.seller} />
                  <VehicleSaleTermsSection isGuaranteed={vehicle.is_guaranteed} />
                  {currentUser.role === roles.BUYER && vehicle.auction.status !== AUCTION_STATUSES.ENDED && (
                    <QuestionSection buyer={currentUser} vehicleId={vehicle.id} currentVehicle={vehicle} />
                  )}
                  <DealerCommentsSection dealerComments={vehicle.dealer_comments} />
                  {vehicle.lister && <ListerAvatarSection lister={vehicle.lister.options ?? {}} />}
                </StyledMainGrid>
              </Grid>
            </PageWrapper>
          )}
          <PageWrapper>{!isLoading && auctionActive && <SuggestionsBlock vehicleId={vehicle.id} />}</PageWrapper>
        </div>
        <SidePanelWrapper hidden={!hasBids}>
          <VehicleSidePanel />
        </SidePanelWrapper>
        <VehicleSidePanelMobile />
      </ContentWrapper>
    ) : null;
  }
}

const mapStateToProps = state => ({
  role: state.user.role,
  currentUser: state.user.user,
  myBids: apiSlice.endpoints.getSidePanelBids.select()(state).data?.rows || [],
  loading: state.vehicles.loading,
  vehicle: state.vehicles.selectedVehicle.data || {},
  offerAuction: legacyOfferAuctionSelector(state, state.vehicles.selectedVehicle.data?.id)
});

const mapDispatchToProps = dispatch => ({
  fetchDetails: (vehicleId, options = {}) =>
    new Promise((resolve, reject) => dispatch(vehicleDetails({ vehicleId, options }, resolve, reject).request)),
  fetchOfferAuction: vehicleId =>
    dispatch(apiSlice.endpoints.fetchActiveOffers.initiate({ vehicleIds: vehicleId ? [Number(vehicleId)] : [] })),
  subscribeToSocket: payload => dispatch(subscribe(payload)),
  unsubscribeFromSocket: () => dispatch(unsubscribe()),
  updateStatus: () => dispatch({ type: WS_VEHICLE_AUCTION_UPDATE, payload: {} }),
  setActiveVehicle: id => dispatch(setActiveVehicleId(id)),
  vehicleNotFound: () =>
    dispatch(
      snackShow({
        hideTime: 3000,
        type: 'error',
        message: 'Vehicle not found.'
      })
    )
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(VehicleDetails));
